import { get, put } from "aws-amplify/api";
import React, { useState, useEffect, useCallback, useMemo } from "react";
import { Alert, Button, Col, Row } from "react-bootstrap";
import { PacmanLoader } from "react-spinners";
import { createColumnHelper } from "@tanstack/react-table";
import { GenericTable } from "components/GenericTable";
import {
  createElementIdFactory,
  createSLMRCASuppressionsElementIds,
} from "utils/elementIds";
import { PAGE_SIZES } from "utils/pageSizeConfig";
import "containers/common/List.css";
import "containers/common/Table.css";

const columnHelper = createColumnHelper();

function formatCreatedDate(value) {
  if (!value) {
    return "";
  }

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return "";
  }

  const day = String(date.getDate()).padStart(2, "0");
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const year = date.getFullYear();

  return `${day}-${month}-${year}`;
}

function SLMRCASuppressionList({ isAuthenticated, user }) {
  const slmRcaSuppressionsIdPrefix = "slm-rca-suppressions-list";

  const [isLoading, setIsLoading] = useState(true);
  const [ui, setUi] = useState({ showAlert: false, errorMessage: "" });
  const [sorting, setSorting] = useState([]);
  const [suppressions, setSuppressions] = useState([]);

  const createId = useMemo(
    () => createElementIdFactory(slmRcaSuppressionsIdPrefix),
    [slmRcaSuppressionsIdPrefix]
  );

  const tableElementIds = useMemo(
    () => createSLMRCASuppressionsElementIds(createId),
    [createId]
  );

  const fetchSuppressions = useCallback(async () => {
    setIsLoading(true);

    try {
      const response = await get({
        apiName: "colin",
        path: "/slmmanualsuppressions",
      }).response;

      const json = await response.body.json();
      setSuppressions(Array.isArray(json) ? json : []);
      setUi({ showAlert: false, errorMessage: "" });
      document.title = "RCA Manual Suppression List";
    } catch (error) {
      console.error("Failed to fetch SLM RCA manual suppressions", error);
      setUi({ showAlert: true, errorMessage: "Failed to load RCA manual suppressions." });
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!isAuthenticated) {
      return;
    }

    fetchSuppressions();
  }, [isAuthenticated, fetchSuppressions]);

  const handleDeleteRecord = useCallback(
    async (record) => {
      const recordId = record?.owner_is_company_keyword_result_id;

      if (!recordId) {
        setUi({ showAlert: true, errorMessage: "Record ID missing. Please refresh the page." });
        return;
      }

      const confirmed = window.confirm(
        `Are you sure you want to delete "${record?.input_value ?? "this record"}"?`
      );

      if (!confirmed) {
        return;
      }

      try {
        const response = await put({
          apiName: "colin",
          path: `/slmmanualsuppressions/${recordId}`,
          options: {
            body: {
              ...record,
              archived: true,
              archived_date: new Date().toISOString(),
              last_edited_by: user,
            },
          },
        }).response;

        const result = await response.body.json();

        if (result?.result === false) {
          setUi({
            showAlert: true,
            errorMessage: result?.error_message || "Failed to delete RCA manual suppression.",
          });
          return;
        }

        setSuppressions((previous) =>
          previous.filter(
            (item) => item?.owner_is_company_keyword_result_id !== recordId
          )
        );
        setUi({ showAlert: false, errorMessage: "" });
      } catch (error) {
        console.error("Failed to delete SLM RCA manual suppression", error);
        setUi({ showAlert: true, errorMessage: "Failed to delete RCA manual suppression." });
      }
    },
    [user]
  );

  const columns = useMemo(
    () => [
      columnHelper.accessor("owner_is_company_keyword_result_id", {
        id: "owner_is_company_keyword_result_id",
        header: "ID",
        cell: (info) => info.getValue() ?? "",
        size: 90,
      }),
      columnHelper.accessor("input_value", {
        id: "input_value",
        header: "Value",
        cell: (info) => info.getValue() ?? "",
        size: 360,
      }),
      columnHelper.accessor("created_date", {
        id: "created_date",
        header: "Created Date",
        cell: (info) => formatCreatedDate(info.getValue()),
        size: 180,
      }),
      columnHelper.display({
        id: "delete_action",
        header: "",
        cell: (info) => (
          <Button
            id={createId("delete", "button", info.row.id)}
            type="button"
            size="sm"
            variant="danger"
            onClick={() => handleDeleteRecord(info.row.original)}
          >
            Delete
          </Button>
        ),
        size: 90,
        enableSorting: false,
      }),
    ],
    [createId, handleDeleteRecord]
  );

  if (!isAuthenticated) {
    return null;
  }

  return (
    <div className="mt-3">
      <Alert
        show={ui.showAlert}
        variant="danger"
        onClose={() => setUi((previous) => ({ ...previous, showAlert: false }))}
        dismissible
      >
        {ui.errorMessage}
      </Alert>

      <Row>
        <Col>
          {isLoading ? (
            <div className="outer-div">
              <div className="sweet-loading inner-div">
                <PacmanLoader
                  cssOverride={{ display: "block", margin: "0 auto" }}
                  size={20}
                  color={"#e03c31"}
                  loading={isLoading}
                />
              </div>
            </div>
          ) : (
            <div className="custom-data-table">
              <GenericTable
                columns={columns}
                data={suppressions}
                section="slm-rca-suppressions-list"
                elementIds={tableElementIds}
                pageSize={PAGE_SIZES.slmLookupRCASuppressions}
                enablePagination={true}
                state={{ sorting }}
                onSortingChange={setSorting}
                enableSortingRemoval={false}
                getRowId={(row) => String(row.owner_is_company_keyword_result_id ?? row.input_value ?? "")}
              />
            </div>
          )}
        </Col>
      </Row>
    </div>
  );
}

export default SLMRCASuppressionList;
